import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { verifyEmailChange, completeEmailChange } from '../utils/emailVerification';
import { auth } from '../utils/firebase';
import Header from '../components/Header';

const EmailVerification = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const handleVerification = async () => {
      try {
        setVerificationStatus('verifying');
        
        // Verify the email change request
        const result = await verifyEmailChange(requestId);
        
        if (result.success) {
          setVerificationStatus('verified');
          setMessage(result.message);
          
          // If user is logged in, complete the auth update
          const currentUser = auth.currentUser;
          if (currentUser) {
            try {
              await completeEmailChange(currentUser, result.newEmail);
              setMessage('Email updated successfully in both database and authentication system!');
            } catch (authError) {
              setMessage('Email updated in database. You may need to re-authenticate to complete the change in your account.');
            }
          } else {
            setMessage('Email updated in database. Please log in to complete the authentication update.');
          }
        } else {
          setVerificationStatus('error');
          setError(result.message);
        }
      } catch (err) {
        setVerificationStatus('error');
        setError('An unexpected error occurred during verification.');
        console.error(err);
      }
    };

    if (requestId) {
      handleVerification();
    }
  }, [requestId]);

  const handleContinue = () => {
    navigate('/home');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-gray-800 p-8 rounded-lg shadow-lg max-w-md w-full mx-4">
          <div className="text-center">
            {verificationStatus === 'verifying' && (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
                <h1 className="text-2xl font-bold mb-4">Verifying Email Change</h1>
                <p className="text-gray-300">Please wait while we verify your email change request...</p>
              </>
            )}

            {verificationStatus === 'verified' && (
              <>
                <div className="text-6xl mb-4">✅</div>
                <h1 className="text-2xl font-bold mb-4 text-green-400">Email Change Verified!</h1>
                <p className="text-gray-300 mb-6">{message}</p>
                <div className="space-y-3">
                  <button
                    onClick={handleContinue}
                    className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold"
                  >
                    Continue to Dashboard
                  </button>
                  {!auth.currentUser && (
                    <button
                      onClick={handleLogin}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
                    >
                      Log In to Complete
                    </button>
                  )}
                </div>
              </>
            )}

            {verificationStatus === 'error' && (
              <>
                <div className="text-6xl mb-4">❌</div>
                <h1 className="text-2xl font-bold mb-4 text-red-400">Verification Failed</h1>
                <p className="text-gray-300 mb-6">{error}</p>
                <div className="space-y-3">
                  <button
                    onClick={handleContinue}
                    className="w-full bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold"
                  >
                    Return to Dashboard
                  </button>
                  <p className="text-sm text-gray-400">
                    If you believe this is an error, please contact support.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification; 